<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="handleOverlayClick">
    <div class="modal-content convert-hidden-modal">
      <!-- Header -->
      <div class="modal-header">
        <h3 class="modal-title">
          <span class="material-symbols-outlined">visibility</span>
          Reveal Hidden Canasta?
        </h3>
        <button
          v-if="closable"
          class="modal-close-btn"
          @click="handleCancel"
          aria-label="Close modal"
        >
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <!-- Progress Display -->
        <div class="progress-display">
          <div class="progress-visual">
            <span
              v-for="i in 7"
              :key="i"
              class="progress-dot"
              :class="{ filled: i <= cardCount }"
            ></span>
          </div>
          <div class="progress-text">{{ cardCount }}/7 wild cards</div>
        </div>

        <!-- Warning Box -->
        <div class="warning-box">
          <div class="warning-icon">
            <span class="material-symbols-outlined">warning</span>
          </div>
          <div class="warning-content">
            <h4>Point Loss Warning</h4>
            <p>
              Revealing your hidden canasta now will score
              <strong class="reveal-points">1,500 points</strong> instead of
              <strong class="hidden-points">2,000 points</strong>.
            </p>
            <p class="hint">
              Wait for {{ 7 - cardCount }} more wild card{{ 7 - cardCount !== 1 ? 's' : '' }}
              to get the full hidden bonus!
            </p>
          </div>
        </div>

        <!-- Point Comparison -->
        <div class="point-comparison">
          <div class="point-option current">
            <div class="point-label">If revealed now</div>
            <div class="point-value">1,500</div>
            <div class="point-hint">Wild canasta bonus</div>
          </div>
          <div class="point-arrow">
            <span class="material-symbols-outlined">arrow_forward</span>
          </div>
          <div class="point-option optimal">
            <div class="point-label">At 7 cards (auto-play)</div>
            <div class="point-value">2,000</div>
            <div class="point-hint">Hidden canasta bonus</div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="modal-actions">
        <button class="btn-primary keep-hidden" @click="handleCancel">
          <span class="material-symbols-outlined">visibility_off</span>
          Keep Hidden
        </button>
        <button class="btn-danger reveal" @click="handleConfirm">
          <span class="material-symbols-outlined">visibility</span>
          Reveal for 1,500 pts
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

/**
 * ConvertHiddenModal Component
 *
 * Modal shown when player clicks on their hidden canasta tracker.
 * Confirms conversion from hidden (2,000 pts potential) to revealed (1,500 pts).
 *
 * @component
 * @example
 * <ConvertHiddenModal
 *   :cardCount="5"
 *   @confirmed="handleConvert"
 *   @cancelled="handleCancelled"
 * />
 */

// ============================================================================
// Props & Emits
// ============================================================================

interface Props {
  cardCount: number
  closable?: boolean
}

interface Emits {
  (e: 'confirmed'): void
  (e: 'cancelled'): void
}

withDefaults(defineProps<Props>(), {
  closable: true,
})

const emit = defineEmits<Emits>()

// ============================================================================
// State
// ============================================================================

const isOpen = ref(true)

// ============================================================================
// Methods
// ============================================================================

/**
 * Handle confirm (reveal the hidden canasta)
 */
function handleConfirm() {
  isOpen.value = false
  emit('confirmed')
}

/**
 * Handle overlay click (close if closable)
 */
function handleOverlayClick() {
  handleCancel()
}

/**
 * Handle cancel (keep hidden)
 */
function handleCancel() {
  isOpen.value = false
  emit('cancelled')
}
</script>

<style scoped>
/* Modal overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: var(--space-4);
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Modal content */
.modal-content {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: var(--radius-lg);
  padding: var(--space-6);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-4);
  padding-bottom: var(--space-3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-title {
  font-size: var(--text-xl);
  font-weight: 700;
  color: #fbbf24;
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.modal-title .material-symbols-outlined {
  font-size: 1.5rem;
}

.modal-close-btn {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary);
}

/* Body */
.modal-body {
  margin-bottom: var(--space-4);
}

/* Progress Display */
.progress-display {
  text-align: center;
  margin-bottom: var(--space-4);
  padding: var(--space-4);
  background: rgba(168, 85, 247, 0.1);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: var(--radius-md);
}

.progress-visual {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: var(--space-2);
}

.progress-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(168, 85, 247, 0.4);
  background: transparent;
  transition: all 0.2s ease;
}

.progress-dot.filled {
  background: linear-gradient(135deg, #a855f7, #8b3dd6);
  border-color: #a855f7;
  box-shadow: 0 0 8px rgba(168, 85, 247, 0.5);
}

.progress-text {
  color: #c4b5fd;
  font-size: var(--text-sm);
  font-weight: 600;
}

/* Warning Box */
.warning-box {
  display: flex;
  gap: var(--space-3);
  padding: var(--space-4);
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-4);
}

.warning-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: rgba(251, 191, 36, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fbbf24;
}

.warning-icon .material-symbols-outlined {
  font-size: 1.25rem;
}

.warning-content h4 {
  margin: 0 0 var(--space-2);
  color: #fbbf24;
  font-size: var(--text-base);
  font-weight: 600;
}

.warning-content p {
  margin: 0 0 var(--space-2);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  line-height: 1.5;
}

.warning-content p:last-child {
  margin-bottom: 0;
}

.reveal-points {
  color: #fbbf24;
}

.hidden-points {
  color: #a855f7;
}

.hint {
  color: #10b981 !important;
  font-style: italic;
}

/* Point Comparison */
.point-comparison {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
}

.point-option {
  flex: 1;
  text-align: center;
  padding: var(--space-3);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md);
}

.point-option.current {
  border-color: rgba(251, 191, 36, 0.3);
}

.point-option.optimal {
  border-color: rgba(168, 85, 247, 0.3);
  background: rgba(168, 85, 247, 0.1);
}

.point-label {
  font-size: var(--text-xs);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: var(--space-1);
}

.point-value {
  font-size: var(--text-2xl);
  font-weight: 700;
}

.point-option.current .point-value {
  color: #fbbf24;
}

.point-option.optimal .point-value {
  color: #a855f7;
}

.point-hint {
  font-size: var(--text-xs);
  color: var(--text-muted);
  margin-top: var(--space-1);
}

.point-arrow {
  color: var(--text-secondary);
  font-size: 1.25rem;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  gap: var(--space-3);
  padding-top: var(--space-4);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-primary,
.btn-danger {
  flex: 1;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  font-weight: 600;
  font-size: var(--text-sm);
  cursor: pointer;
  transition: all var(--transition-base);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
}

.btn-primary.keep-hidden {
  background: linear-gradient(135deg, #a855f7 0%, #8b3dd6 100%);
  color: white;
}

.btn-primary.keep-hidden:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(168, 85, 247, 0.4);
}

.btn-danger.reveal {
  background: rgba(251, 191, 36, 0.2);
  color: #fbbf24;
  border: 1px solid rgba(251, 191, 36, 0.3);
}

.btn-danger.reveal:hover {
  background: rgba(251, 191, 36, 0.3);
}

.btn-primary .material-symbols-outlined,
.btn-danger .material-symbols-outlined {
  font-size: 1.1rem;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    padding: var(--space-4);
    max-height: 95vh;
  }

  .point-comparison {
    flex-direction: column;
    gap: var(--space-2);
  }

  .point-arrow {
    transform: rotate(90deg);
  }

  .modal-actions {
    flex-direction: column;
  }
}
</style>
